import { Timestamp } from 'firebase/firestore';

/**
 * VacationModel - Represents a single vacation day for a staff member
 * Each vacation day is stored as a separate document in Firestore
 * 
 * Collection Path: companies/{companyId}/vacations/{vacationId}
 * 
 * IMPORTANT: 
 * - Dates are stored as strings in "YYYY-MM-DD" format to avoid timezone issues
 * - Duration is stored in MINUTES (not hours) for precision and consistency with shifts
 */
export class VacationModel {
  constructor({
    id = null,
    staffId,
    staffName = '',
    departmentId,
    departmentName = '',
    date,
    durationMinutes = 0,
    hours = null, // Legacy field - auto-converted to durationMinutes
    createdBy,
    createdAt = new Date(),
    updatedAt = null,
    updatedBy = null,
    lastRecalculatedAt = null
  }) {
    this.id = id;
    this.staffId = staffId;
    this.staffName = staffName; // Denormalized for display (not stored in Firestore)
    this.departmentId = departmentId;
    this.departmentName = departmentName; // Denormalized for display (not stored in Firestore)
    
    // Store date as string in YYYY-MM-DD format
    this.date = this._toDateString(date);
    
    // Handle legacy hours field conversion
    if (hours !== null && durationMinutes === 0) {
      this.durationMinutes = Math.round(hours * 60);
    } else {
      this.durationMinutes = durationMinutes;
    }
    
    this.createdBy = createdBy;
    this.createdAt = createdAt instanceof Date ? createdAt : createdAt?.toDate();
    this.updatedAt = updatedAt instanceof Date ? updatedAt : updatedAt?.toDate();
    this.updatedBy = updatedBy;
    this.lastRecalculatedAt = lastRecalculatedAt instanceof Date ? lastRecalculatedAt : lastRecalculatedAt?.toDate();
  }

  /**
   * Convert date to YYYY-MM-DD string format
   * @private
   */
  _toDateString(date) {
    if (!date) return null;
    
    // Already a string in correct format
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    
    // Convert Date object to string
    if (date instanceof Date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    // Firestore Timestamp
    if (date.toDate && typeof date.toDate === 'function') {
      const d = date.toDate();
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    // Try parsing as date string
    try {
      const d = new Date(date);
      if (!isNaN(d.getTime())) {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    } catch (e) {
      console.error('Invalid date format:', date);
    }
    
    return null;
  }

  /**
   * Convert date string to Date object for display
   * @private
   */
  _toDateObject() {
    if (!this.date) return null;
    // Parse YYYY-MM-DD as UTC to avoid timezone issues
    const [year, month, day] = this.date.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  /**
   * Convert vacation model to Firestore document format
   */
  toFirestore() {
    return {
      staffId: this.staffId,
      departmentId: this.departmentId,
      date: this.date, // Store as string
      durationMinutes: this.durationMinutes,
      createdBy: this.createdBy,
      createdAt: Timestamp.fromDate(this.createdAt),
      ...(this.updatedAt && { updatedAt: Timestamp.fromDate(this.updatedAt) }),
      ...(this.updatedBy && { updatedBy: this.updatedBy }),
      ...(this.lastRecalculatedAt && { lastRecalculatedAt: Timestamp.fromDate(this.lastRecalculatedAt) })
    };
  }

  /**
   * Create vacation model from Firestore document
   */
  static fromFirestore(doc) {
    if (!doc.exists()) return null;
    
    const data = doc.data();
    
    // Handle legacy data with hours field
    let durationMinutes = data.durationMinutes || 0;
    if (!data.durationMinutes && data.hours !== undefined) {
      durationMinutes = Math.round(data.hours * 60);
    }
    
    return new VacationModel({
      id: doc.id,
      staffId: data.staffId,
      departmentId: data.departmentId,
      date: data.date, // Already a string from Firestore
      durationMinutes: durationMinutes,
      createdBy: data.createdBy,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
      updatedBy: data.updatedBy,
      lastRecalculatedAt: data.lastRecalculatedAt?.toDate()
    });
  }

  /**
   * Get formatted date string (DD.MM.YYYY)
   */
  getFormattedDate() {
    const dateObj = this._toDateObject();
    if (!dateObj) return '';
    
    return dateObj.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  /**
   * Get date string in YYYY-MM-DD format (for comparisons)
   */
  getDateString() {
    return this.date;
  }

  /**
   * Get formatted duration string (e.g., "8h", "7h 45min")
   */
  getFormattedDuration() {
    return VacationHelpers.formatDurationFromMinutes(this.durationMinutes);
  }

  /**
   * Get duration in hours (for legacy compatibility or display)
   */
  getDurationInHours() {
    return this.durationMinutes / 60;
  }

  /**
   * Check if vacation has no scheduled shift
   */
  hasNoShift() {
    return this.durationMinutes === 0;
  }

  /**
   * Get display text for duration
   */
  getDurationDisplayText() {
    if (this.hasNoShift()) {
      return '0 Stunden (Kein Dienst geplant)';
    }
    return `${this.getFormattedDuration()} (${this.durationMinutes} Minuten)`;
  }

  /**
   * Get short display text for duration (just the time)
   */
  getDurationShortText() {
    if (this.hasNoShift()) {
      return '0h';
    }
    return this.getFormattedDuration();
  }

  /**
   * Update duration from shift minutes
   */
  updateFromShift(shiftDurationMinutes) {
    this.durationMinutes = shiftDurationMinutes || 0;
    this.lastRecalculatedAt = new Date();
    this.updatedAt = new Date();
  }

  /**
   * Reset duration to 0 (when shift is deleted)
   */
  resetDuration() {
    this.durationMinutes = 0;
    this.lastRecalculatedAt = new Date();
    this.updatedAt = new Date();
  }
}

/**
 * VacationDateRange - Helper class for creating multiple vacation days
 * Used when adding vacation via date range picker
 */
export class VacationDateRange {
  constructor(startDate, endDate) {
    this.startDate = this._parseDate(startDate);
    this.endDate = this._parseDate(endDate);
  }

  /**
   * Parse date to Date object
   * @private
   */
  _parseDate(date) {
    if (date instanceof Date) {
      return date;
    }
    
    // Firestore Timestamp
    if (date?.toDate && typeof date.toDate === 'function') {
      return date.toDate();
    }
    
    // String format
    if (typeof date === 'string') {
      // Parse YYYY-MM-DD format
      const [year, month, day] = date.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    
    return new Date(date);
  }

  /**
   * Get array of dates between start and end (inclusive)
   */
  getDates() {
    const dates = [];
    const currentDate = new Date(this.startDate);
    
    while (currentDate <= this.endDate) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  }

  /**
   * Get total number of days
   */
  getTotalDays() {
    return this.getDates().length;
  }

  /**
   * Validate date range
   */
  isValid() {
    return this.startDate <= this.endDate;
  }

  /**
   * Check if range is too long (warning threshold)
   */
  isTooLong(maxDays = 10) {
    return this.getTotalDays() > maxDays;
  }

  /**
   * Check if date is too far in future (warning threshold)
   */
  isFarFuture(maxYears = 2) {
    const futureLimit = new Date();
    futureLimit.setFullYear(futureLimit.getFullYear() + maxYears);
    return this.endDate > futureLimit;
  }
}

/**
 * VacationSummary - Summary statistics for vacations
 */
export class VacationSummary {
  constructor() {
    this.totalDays = 0;
    this.totalMinutes = 0;
    this.byStaff = new Map(); // staffId -> { name, days, minutes }
  }

  /**
   * Add vacation to summary
   */
  addVacation(vacation, staffName) {
    this.totalDays++;
    this.totalMinutes += vacation.durationMinutes;

    if (!this.byStaff.has(vacation.staffId)) {
      this.byStaff.set(vacation.staffId, {
        staffId: vacation.staffId,
        name: staffName,
        days: 0,
        minutes: 0
      });
    }

    const staffStats = this.byStaff.get(vacation.staffId);
    staffStats.days++;
    staffStats.minutes += vacation.durationMinutes;
  }

  /**
   * Get top N staff members by vacation days
   */
  getTopStaff(limit = 3) {
    return Array.from(this.byStaff.values())
      .sort((a, b) => b.days - a.days)
      .slice(0, limit);
  }

  /**
   * Get formatted total duration
   */
  getFormattedTotalDuration() {
    return VacationHelpers.formatDurationFromMinutes(this.totalMinutes);
  }

  /**
   * Get total duration in hours (for display)
   */
  getTotalHours() {
    return this.totalMinutes / 60;
  }

  /**
   * Get formatted total as hours (legacy format)
   */
  getFormattedTotalHours() {
    const hours = this.totalMinutes / 60;
    return `${hours.toFixed(1)} Stunden`;
  }
}

/**
 * Validation functions
 */
export const VacationValidation = {
  /**
   * Validate vacation data before save
   */
  validate(vacationData) {
    const errors = [];

    if (!vacationData.staffId) {
      errors.push('Mitarbeiter ist erforderlich');
    }

    if (!vacationData.departmentId) {
      errors.push('Abteilung ist erforderlich');
    }

    if (!vacationData.date) {
      errors.push('Datum ist erforderlich');
    }

    if (vacationData.durationMinutes < 0) {
      errors.push('Dauer kann nicht negativ sein');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  },

  /**
   * Check if date range is valid
   */
  validateDateRange(startDate, endDate) {
    const errors = [];

    if (!startDate || !endDate) {
      errors.push('Start- und Enddatum sind erforderlich');
      return { isValid: false, errors };
    }

    if (startDate > endDate) {
      errors.push('Enddatum muss nach Startdatum liegen');
    }

    const range = new VacationDateRange(startDate, endDate);
    
    if (range.isTooLong()) {
      // Warning, not error
      errors.push({
        type: 'warning',
        message: 'Mehr als 10 aufeinanderfolgende Urlaubstage'
      });
    }

    if (range.isFarFuture()) {
      errors.push({
        type: 'warning',
        message: 'Datum liegt mehr als 2 Jahre in der Zukunft'
      });
    }

    return {
      isValid: errors.filter(e => typeof e === 'string').length === 0,
      errors
    };
  }
};

/**
 * Helper functions
 */
export const VacationHelpers = {
  /**
   * Format duration from minutes to human-readable format
   * Examples:
   * - 0 min -> "0h"
   * - 60 min -> "1h"
   * - 90 min -> "1h 30min"
   * - 465 min -> "7h 45min"
   * - 480 min -> "8h"
   */
  formatDurationFromMinutes(minutes) {
    if (!minutes || minutes === 0) {
      return '0h';
    }

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (mins === 0) {
      return `${hours}h`;
    }

    return `${hours}h ${mins}min`;
  },

  /**
   * Format duration from hours to human-readable format (legacy support)
   * @deprecated Use formatDurationFromMinutes instead
   */
  formatDurationFromHours(hours) {
    const minutes = Math.round(hours * 60);
    return this.formatDurationFromMinutes(minutes);
  },

  /**
   * Convert minutes to hours (decimal)
   */
  minutesToHours(minutes) {
    return minutes / 60;
  },

  /**
   * Convert hours to minutes
   */
  hoursToMinutes(hours) {
    return Math.round(hours * 60);
  },

  /**
   * Generate vacation ID (date-based for easy querying)
   * Format: {staffId}_{YYYY-MM-DD}_{departmentId}
   */
  generateVacationId(staffId, date, departmentId) {
    let dateStr;
    
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      dateStr = date;
    } else if (date instanceof Date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      dateStr = `${year}-${month}-${day}`;
    } else if (date?.toDate && typeof date.toDate === 'function') {
      const d = date.toDate();
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      dateStr = `${year}-${month}-${day}`;
    } else {
      dateStr = date;
    }
    
    return `${staffId}_${dateStr}_${departmentId}`;
  },

  /**
   * Parse vacation ID back to components
   */
  parseVacationId(vacationId) {
    const parts = vacationId.split('_');
    return {
      staffId: parts[0],
      dateStr: parts[1],
      departmentId: parts[2]
    };
  },

  /**
   * Format date for display
   * Accepts string "YYYY-MM-DD" or Date object
   */
  formatDate(date) {
    let dateObj;
    
    if (typeof date === 'string') {
      const [year, month, day] = date.split('-').map(Number);
      dateObj = new Date(year, month - 1, day);
    } else if (date instanceof Date) {
      dateObj = date;
    } else if (date?.toDate && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    } else {
      return '';
    }
    
    return dateObj.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  },

  /**
   * Format date with day name
   * Accepts string "YYYY-MM-DD" or Date object
   */
  formatDateWithDay(date) {
    let dateObj;
    
    if (typeof date === 'string') {
      const [year, month, day] = date.split('-').map(Number);
      dateObj = new Date(year, month - 1, day);
    } else if (date instanceof Date) {
      dateObj = date;
    } else if (date?.toDate && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    } else {
      return '';
    }
    
    return dateObj.toLocaleDateString('de-DE', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  },

  /**
   * Get week number for a date
   */
  getWeekNumber(date) {
    let dateObj;
    
    if (typeof date === 'string') {
      const [year, month, day] = date.split('-').map(Number);
      dateObj = new Date(year, month - 1, day);
    } else if (date instanceof Date) {
      dateObj = date;
    } else {
      dateObj = new Date(date);
    }
    
    const firstDayOfYear = new Date(dateObj.getFullYear(), 0, 1);
    const pastDaysOfYear = (dateObj - firstDayOfYear) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  },

  /**
   * Check if two dates are the same day
   * Accepts string "YYYY-MM-DD" or Date objects
   */
  isSameDay(date1, date2) {
    const str1 = typeof date1 === 'string' ? date1 : 
      date1 instanceof Date ? 
        `${date1.getFullYear()}-${String(date1.getMonth() + 1).padStart(2, '0')}-${String(date1.getDate()).padStart(2, '0')}` :
        '';
    
    const str2 = typeof date2 === 'string' ? date2 : 
      date2 instanceof Date ? 
        `${date2.getFullYear()}-${String(date2.getMonth() + 1).padStart(2, '0')}-${String(date2.getDate()).padStart(2, '0')}` :
        '';
    
    return str1 === str2;
  }
};

export default VacationModel;